// @flow

// These should match the @font-face declarations (see jsx-content-fonts.css)
// This ensures that we cannot accidentally use a font name which is not
// actually registered, nor a weight which is not actually registered.
export const fontFamilies = {
  gilroy: {
    name: 'Gilroy',
    weights: {
      light: 300,
      extraBold: 800,
    },
  },
  proximaNova: {
    name: 'Proxima Nova',
    weights: {
      regular: 400,
      semibold: 600,
      bold: 700,
    },
  },
};

// NOTE: we refer to "figure" sizes, rather than image sizes
// because we have a bunch of image-like components that must
// adhere to these sizes, but are not strictly images:
// CartesianPlane, NumberLine, BoxAndWhiskerPlot, etc.

const figureWidths = [
  116, // 5 columns in the grid system
  260, // 11 columns in the grid system
  596, // 25 columns in the grid system
];

const figureSizes = [
  // 116 width variants
  { width: 116, height: 116 },

  // 260 width variants
  { width: 260, height: 160 },
  { width: 260, height: 200 },
  { width: 260, height: 260 },
  { width: 260, height: 360 },

  // 596 width variants
  { width: 596, height: 160 },
  { width: 596, height: 200 },
  { width: 596, height: 260 },
  { width: 596, height: 360 },
];

// See https://www.figma.com/file/2SuTQvoqSK2CHryyxqGvid/DLS-%E2%80%93-Numero-2020?node-id=248%3A146
const illustrationColor = {
  // monochrome theme
  cloudburst: '#21374D',
  pickledBluewood: '#34495E',
  shuttleGrey: '#5E6D7F',
  regentGray: '#7E8D9C',
  grayChateau: '#99A4AF',
  iron: '#E5E6E8',
  porcelain: '#F1F2F3',
  seashell: '#F1F1F1',

  // astro theme
  astronaut: '#26276D',
  bayOfMany: '#2E3696',
  governorBay: '#3B4DBA',
  cornflowerBlue: '#535FEE',
  persia: '#838CF8',
  perano: '#A9BAF3',
  snuffBlue: '#D5DDF9',

  // blue theme
  regalBlue: '#003960',
  bahamaBlue: '#005894',
  lochmara: '#0875BE',
  shakespeare: '#3991CB',
  viking: '#66B1E2',
  jordyBlue: '#A5CDF2',
  tropicalBlue: '#D5E8F9',

  // teal theme
  eden: '#03353A',
  elm: '#186C75',
  blueChill: '#0694A4',
  java: '#1CB7C8',
  scooter: '#36C8D8',
  waterLeaf: '#9EDAE2',
  jaggedIce: '#CBE8EC',

  // green theme
  gableGreen: '#154235',
  genoa: '#295D4E',
  elfGreen: '#00856B',
  niagra: '#03A887',
  mountainMeadow: '#1ABC9C',
  morningGlory: '#ACE3D9',
  iceberg: '#D1EEE7',

  // tan theme
  paco: '#3B1C0C',
  cinnamon: '#6C442F',
  tangerine: '#E89D00',
  saffron: '#F4B42E',
  manhattan: '#F8C983',
  lightApricot: '#FDDFB3',
  papayaWhip: '#FDF0D5',

  // brick theme
  peru: '#B44000',
  hotCinnamon: '#DE590F',
  crusta: '#FD7B33',
  atomicTangerine: '#FF9D66',
  hitPink: '#FFB68C',
  lightPeach: '#FFD8C2',
  serenade: '#FFEDE2',

  // red theme
  redBerry: '#8F011B',
  tallPoppy: '#C11B39',
  brickRed: '#CF3551',
  geraldine: '#F87F8D',
  roseBud: '#FFA8B2',
  watusi: '#FFD6DA',
  provincialPink: '#FAECE9',

  // purple theme
  blueGem: '#300391',
  grape: '#4B16BE',
  purpleHeart: '#7547D8',
  portage: '#A58BF9',
  melrose: '#B59FFC',
  prelude: '#CDC9EA',
  snuff: '#E1E2F1',
};

// see https://www.figma.com/file/Z9ezI0vGPtHGauLtMTgQD5/Library-%E2%80%93-Product?node-id=0%3A1
const productColor = {
  // monochrome
  cloudburst: '#21374D',
  shuttleGrey: '#5E6D7F',
  grayChateau: '#99A4AF',
  ironDark: '#D4D7D9',
  iron: '#E5E6E8',
  seashell: '#F1F1F1',
  white: '#FFFFFF',

  // blues
  matisse: '#195E8C',
  lochmara: '#0875BE',
  tropicalBlue: '#D5E8F9',
  aliceBlue: '#F3F9FF',

  // purples
  astronaut: '#26276D',
  bayOfMany: '#2E3696',
  governorBay: '#3B4DBA',
  snuff: '#D8DAE9',

  // eggplants
  eggplant100: '#5D51AE',
  eggplant90: '#8C83C7',
  eggplant20: '#D7D6E8',
  eggplant10: '#EFEEF7',

  // oranges
  crusta: '#FD7B33',
  serenade: '#FFEDE2',
  almond: '#E0DBCF',
  almond50: '#F7F5F1',

  //marking
  mountainMeadow: '#1ABC9C',
  iceberg: '#D1EEE7',
  brickRed: '#CF3551',
  provincialPink: '#FAECE9',
  saffron: '#F4B42E',
  papayaWhip: '#FDF0D5',
};

const productColorSunflower = {
  // Primary
  eggplant: '#5D51AE',
  eggplant90: '#8C83C7',
  eggplant20: '#D7D6E8',
  eggplant10: '#EFEEF7',

  // Text
  grey: '#3E3E4C',
  grey90: '#6F6F82',
  grey20: '#C7C5C6',
  white: '#FFFFFF',

  // Decorators
  ironDark: '#D4D7D9',
  iron: '#E5E6E8',
  seashell: '#F8F8F8',
  almond: '#E0DBCF',
  almond50: '#F7F5F1',

  // Grading
  pine: '#4A7068',
  pine20: '#A3BFBA',
  peachPink: '#D25564',
  peachPink20: '#F0CDC9',
  gold: '#E9B84E',
  gold20: '#E6CFA3',

  // positive
  mountainMeadow: '#1ABC9C',
  iceberg: '#CAEFE7',

  // negative
  brickRed: '#CF3551',
  provincialPink: '#FDEBE8',

  // warning
  saffron: '#F4B42E',
  papayaWhip: '#FDF0D5',

  // notification
  crusta: '#F47936',
  serenade: '#FFEDE2',

  // task progress
  lochmara: '#0875BE',
  tropicalBlue: '#D5E8F9',
};

type IllustrationColor = $Keys<typeof illustrationColor>;
type ProductColor = $Keys<typeof productColor>;

export type { IllustrationColor };
export type { ProductColor };
export {
  figureWidths,
  figureSizes,
  illustrationColor,
  productColor,
  productColorSunflower,
};
